import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useColonyAuth } from "@/contexts/ColonyAuthContext";
import { Button } from "@/components/ui/button";
import { Home, Wrench, LogOut, FileText, Megaphone, Users } from "lucide-react";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { useColonyBrand } from "./useColonyBrand";
import { PortalInvitationsDialog } from "@/components/onboarding/PortalInvitationsDialog";
import { useStayPut } from "@/lib/useStayPut";
import { useHeaderHeightVar } from "@/lib/useHeaderHeightVar";
import PushToggle from "@/components/PushToggle";
import InstallAppButton from "@/components/InstallAppButton";
import { colonyApi } from "@/lib/colonyApi";

export default function ColonyLayout() {
  useColonyBrand();
  useStayPut();
  const { user, logout } = useColonyAuth();
  const [invitationsOpen,setInvitationsOpen]=React.useState(()=>new URLSearchParams(window.location.search).get("invitations")==="1");
  const nav = useNavigate();
  const headerRef = useHeaderHeightVar();
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);

  React.useEffect(() => {
    document.body.classList.add("colony-portal-shell");
    return () => document.body.classList.remove("colony-portal-shell");
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/managementusa", { replace: true });
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 rounded-sm text-xs sm:text-sm font-medium transition whitespace-nowrap shrink-0 ${
      isActive ? "bg-emerald-100 text-emerald-900" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-emerald-50/40">
      {/* Header stays in normal flow on phones; only the ticket status bar pins. */}
      <header ref={headerRef} className="colony-pwa-header sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur" data-testid="colony-header">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-2 shrink-0" data-testid="colony-brand">
            <Home className="w-5 h-5 text-emerald-700 self-center" />
            <span className="text-lg font-black tracking-tighter text-emerald-800 font-display">BUNGALOW</span>
            <span className="text-xs font-light tracking-wide text-muted-foreground hidden sm:inline">PORTAL</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <InstallAppButton manifest="/manifest-colony.json" appleIcon="/mgmt-apple-touch.png" appName="the Bungalow Colony portal" iosTitle="Bungalow" className="colony-install-button h-9" />
            <PushToggle client={colonyApi} base="/colony/push" targetUrl="/colonies/tickets" testidPrefix="colony-push" />
            <div className="hidden sm:block text-right">
              <div className="text-xs text-muted-foreground">Signed in as</div>
              <div className="text-sm font-medium" data-testid="colony-me-label">{user?.name || user?.email}</div>
            </div>
            <Button variant="outline" size="sm" className="colony-signout-button rounded-sm" onClick={handleLogout} aria-label="Sign out" data-testid="colony-logout">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-2 sm:px-4 pb-2 flex flex-wrap gap-0.5 sm:gap-1" data-testid="colony-primary-nav">
          <NavLink to="/colonies/tickets" className={linkCls} data-testid="colony-nav-tickets">
            <Wrench className="w-4 h-4" /> <span className="sm:hidden">Requests</span><span className="hidden sm:inline">Service requests</span>
          </NavLink>
          <NavLink to="/colonies/statement" className={linkCls} data-testid="colony-nav-statement">
            <FileText className="w-4 h-4" /> Statement
          </NavLink>
          <button type="button" className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm text-xs sm:text-sm font-semibold min-h-[44px] text-muted-foreground hover:bg-secondary/50" onClick={()=>setInvitationsOpen(true)} data-testid="colony-invitations-open"><Users className="h-4 w-4"/>Invite manager</button>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 rounded-sm text-xs sm:text-sm font-medium transition whitespace-nowrap shrink-0 text-muted-foreground hover:bg-secondary hover:text-foreground"
            data-testid="colony-nav-feedback"
          >
            <Megaphone className="w-4 h-4" /> <span className="sm:hidden">Report</span><span className="hidden sm:inline">Report issue</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6" data-testid="colony-main">
        <Outlet />
      </main>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} client={colonyApi} base="/colony/feedback" />
      <PortalInvitationsDialog open={invitationsOpen} onOpenChange={setInvitationsOpen} colonyMode/>
    </div>
  );
}
